import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/base/VideoCard";
import Pagination from "@/components/base/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categoryMeta, videos, type VideoType } from "@/mocks/videos";
import CategoryFilter, { type FilterState } from "./components/CategoryFilter";

const PAGE_SIZE = 8;

const validTypes: VideoType[] = ["movie", "drama", "variety", "short"];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const isValid = validTypes.includes(type as VideoType);
  const meta = isValid ? categoryMeta[type as VideoType] : null;

  const [filter, setFilter] = useState<FilterState>({
    year: "全部",
    region: "全部",
    sort: "default",
  });
  const [page, setPage] = useState(1);
  const [mobileFilter, setMobileFilter] = useState(false);

  usePageMeta({
    title: meta ? `${meta.label} - 在线视频` : "视频分类 - 在线视频",
    description: meta
      ? `${meta.intro}`
      : "在线视频为您提供电影、电视剧、综艺和短视频分类浏览。",
    keywords: meta
      ? `${meta.label},在线视频,${meta.label}在线观看,视频分类`
      : "在线视频,视频分类",
  });

  useEffect(() => {
    setPage(1);
  }, [filter.year, filter.region, filter.sort, type]);

  const filtered = useMemo(() => {
    if (!isValid) return [];
    let list = videos.filter((v) => v.type === type);
    if (filter.year !== "全部") list = list.filter((v) => v.year === filter.year);
    if (filter.region !== "全部")
      list = list.filter((v) => v.region === filter.region);
    if (filter.sort === "newest")
      list = [...list].sort(
        (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime(),
      );
    else if (filter.sort === "hot")
      list = [...list].sort((a, b) => b.playCount - a.playCount);
    return list;
  }, [filter, type, isValid]);

  if (!isValid || !meta) {
    return <Navigate to="/404" replace />;
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const isFiltered =
    filter.year !== "全部" || filter.region !== "全部" || filter.sort !== "default";

  const resetFilters = () => {
    setFilter({ year: "全部", region: "全部", sort: "default" });
    setPage(1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-8">
        <div className="max-w-[1200px] mx-auto">
          {/* Breadcrumb */}
          <nav
            aria-label="面包屑"
            className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5"
          >
            <Link to="/" className="hover:text-primary-600 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium">{meta.label}</span>
          </nav>

          {/* Title */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {meta.label}
              </h1>
              <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
                {meta.intro}
              </p>
            </div>
            <span className="shrink-0 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-sm font-medium whitespace-nowrap">
              共 {filtered.length} 部内容
            </span>
          </div>

          <div className="flex gap-6 items-start">
            <CategoryFilter
              value={filter}
              onChange={setFilter}
              mobileOpen={mobileFilter}
              onCloseMobile={() => setMobileFilter(false)}
            />

            <div className="flex-1 min-w-0">
              {/* Mobile filter trigger */}
              <div className="flex items-center justify-between gap-3 mb-4 lg:hidden">
                <button
                  type="button"
                  onClick={() => setMobileFilter(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 whitespace-nowrap hover:border-primary-400 cursor-pointer"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选
                </button>
                <span className="text-sm text-foreground-500">
                  {filtered.length} 部内容
                </span>
              </div>

              {pageItems.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                    {pageItems.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                  <Pagination
                    current={currentPage}
                    total={totalPages}
                    onChange={(p) => {
                      setPage(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 px-6 rounded-lg bg-background-50 border border-dashed border-background-300 text-center">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-emotion-sad-line text-3xl"></i>
                  </span>
                  <h3 className="mt-5 text-lg font-semibold text-foreground-800">
                    暂无该分类视频，换个条件试试
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500">
                    当前筛选条件下没有匹配的内容，试试调整年份或地区。
                  </p>
                  {isFiltered && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer"
                    >
                      <i className="ri-restart-line"></i>
                      重置筛选
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}